import Base from '../base/Base';
import HwzComponent from '../../component/HwzComponent';
import Moon from '../global/Moon';
import Lang from '../global/Lang';
import HttpBase from '../base/HttpBase'; // 导入 HttpBase 基类

export default class RemovableStorage extends Base {
  uuid: string = '';
  deviceId: string = '';
  name: string = '';
  mountPoint: string = '';
  totalSize: number = 0;
  freeSize: number = 0;
  usedSize: number = 0;
  fileSystem: string = '';
  connectedAt: string = '';
  lastSeenAt: string = '';
  status: string = 'connected';
  isRemovable: boolean = true;

  safeAssign(data: any): this {
    if (data) {
      // 优先处理小写字段（因为后台返回的是小写）
      this.uuid = data.uuid || data.UUID || '';
      this.deviceId = data.deviceId || data.DeviceID || data.deviceID || data.id || '';
      this.name = data.name || data.Name || data.Label || data.label || '未命名设备';
      this.mountPoint = data.mountPoint || data.MountPoint || data.mountpoint || data.mount || '';
      this.totalSize = data.totalSize || data.TotalSize || data.total || data.size || 0;
      this.freeSize = data.freeSize || data.FreeSize || data.free || 0;
      this.usedSize = data.usedSize || data.UsedSize || data.used || 0;
      this.fileSystem = data.fileSystem || data.FileSystem || data.filesystem || data.fstype || '';
      this.status = data.status || data.Status || data.state || 'connected';
      this.isRemovable = data.isRemovable !== undefined ? data.isRemovable : 
                        (data.IsRemovable !== undefined ? data.IsRemovable : true);
      
      // 处理时间字段
      this.connectedAt = data.connectedAt || data.ConnectedAt || data.connectAt || data.createdAt || '';
      this.lastSeenAt = data.lastSeenAt || data.LastSeenAt || data.lastSeen || data.lastCheck || '';
      
      // 计算 usedSize 如果没有提供
      if (this.usedSize === 0 && this.totalSize > 0 && this.freeSize > 0) {
        this.usedSize = this.totalSize - this.freeSize;
      }
    }
    
    return this;
  }
  // 格式化显示的大小
  getFormattedTotalSize(): string {
    return this.formatSize(this.totalSize);
  }

  getFormattedFreeSize(): string {
    return this.formatSize(this.freeSize);
  }

  getFormattedUsedSize(): string {
    return this.formatSize(this.totalSize - this.freeSize);
  }

  getUsedPercentage(): number {
    if (this.totalSize === 0) return 0;
    return Math.round(((this.totalSize - this.freeSize) / this.totalSize) * 100);
  }

  // 获取设备状态标签
  getStatusTag(): { color: string; text: string } {
    switch (this.status) {
      case 'connected':
        return { color: 'success', text: Lang.t('removable.statusConnected') };
      case 'disconnected':
        return { color: 'error', text: Lang.t('removable.statusDisconnected') };
      case 'error':
        return { color: 'warning', text: Lang.t('removable.statusError') };
      default:
        return { color: 'default', text: this.status };
    }
  }

  // 静态方法：根据状态获取标签
  static getStatusTagByStatus(status: string): { color: string; text: string } {
    switch (status) {
      case 'connected':
        return { color: 'success', text: Lang.t('removable.statusConnected') };
      case 'disconnected':
        return { color: 'error', text: Lang.t('removable.statusDisconnected') };
      case 'error':
        return { color: 'warning', text: Lang.t('removable.statusError') };
      default:
        return { color: 'default', text: status };
    }
  }

  // 获取连接时间
  getConnectedTime(): string {
    return new Date(this.connectedAt).toLocaleString();
  }

  // 获取最后检测时间
  getLastSeenTime(): string {
    return new Date(this.lastSeenAt).toLocaleString();
  }

  // 判断是否为外部设备
  isExternal(): boolean {
    return this.isRemovable;
  }

  httpList(callback?: Function, errorCallback?: Function) {
    const httpBase = new HttpBase();
    
    console.log('RemovableStorage: 开始请求可移动设备列表...');
    
    httpBase.httpGet(
      '/api/removable/list',
      {},
      (response: any) => {
        console.log('RemovableStorage: 获取设备列表完整响应:', response);
        console.log('RemovableStorage: 响应类型:', typeof response);
        
        // 注意：这里的 response 是完整的响应对象
        // 我们需要检查 response.data 来获取实际数据
        console.log('RemovableStorage: response.data:', response?.data);
        console.log('RemovableStorage: response.data 类型:', typeof response?.data);
        
        let deviceArray = [];
        
        // 情况1：response.data 本身就是数组
        if (response && response.data && Array.isArray(response.data)) {
          console.log('RemovableStorage: response.data 是数组，直接作为设备列表');
          deviceArray = response.data;
        }
        // 情况2：response.data 是对象，但可能包含 data 字段
        else if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
          console.log('RemovableStorage: response.data.data 是数组');
          deviceArray = response.data.data;
        }
        // 情况3：response.data 是对象，但可能是单个设备
        else if (response && response.data && typeof response.data === 'object') {
          console.log('RemovableStorage: response.data 是对象，尝试转换为数组');
          // 检查是否有 DeviceID 或 deviceId 字段
          if (response.data.DeviceID || response.data.deviceId) {
            deviceArray = [response.data];
          }
        }
        // 情况4：response 本身是数组（不太可能，但处理一下）
        else if (Array.isArray(response)) {
          console.log('RemovableStorage: 响应本身是数组');
          deviceArray = response;
        }
        
        console.log('RemovableStorage: 解析后的设备数组:', deviceArray);
        console.log('RemovableStorage: 设备数量:', deviceArray.length);
        
        // 检查响应码
        const responseCode = response?.data?.code || response?.code;
        const isSuccess = responseCode === "OK" || responseCode === "SUCCESS" || responseCode === 200;
        
        if (isSuccess) {
          console.log('RemovableStorage: 响应码为成功，返回设备数组');
          if (callback) {
            callback({
              code: "OK",
              data: deviceArray,
              message: response?.data?.message || response?.data?.msg || "成功"
            });
          }
        } else {
          const errorMsg = response?.data?.message || response?.data?.msg || '获取设备列表失败';
          console.error('RemovableStorage: 响应不成功:', errorMsg);
          errorCallback && errorCallback(errorMsg);
        }
      },
      (err: any) => {
        console.error('RemovableStorage: 获取可移动设备列表网络错误:', err);
        const errorMsg = err && (err.message || err.msg) || '网络错误';
        errorCallback && errorCallback(errorMsg);
      }
    );
  }
// 修复的 browse 静态方法 - 使用 HttpBase 实例
static httpBrowse(deviceId: string, path: string, callback?: Function, errorCallback?: Function) {
  const httpBase = new HttpBase();
  const params: any = { deviceId };
  if (path) {
    params.path = path;
  }
  
  console.log('RemovableStorage.httpBrowse: 请求参数:', params);
  
  httpBase.httpGet(
    '/api/removable/browse',
    params,
    (response: any) => {
      console.log('RemovableStorage.httpBrowse: 完整响应:', response);
      console.log('RemovableStorage.httpBrowse: response.data:', response?.data);
      
      // 注意：HttpBase 的 httpGet 回调接收的是完整的响应对象
      // 我们需要检查 response.data.code
      if (response && response.data && response.data.code === "OK") {
        console.log('RemovableStorage.httpBrowse: 成功获取文件列表');
        callback && callback(response.data.data);
      } else {
        const errorMsg = response?.data?.message || response?.data?.msg || '请求失败';
        console.error('RemovableStorage.httpBrowse: 请求失败:', errorMsg);
        errorCallback && errorCallback(errorMsg);
      }
    },
    (err: any) => {
      const errorMsg = err && (err.message || err.msg) || '网络错误';
      console.error('RemovableStorage.httpBrowse: 网络错误:', errorMsg);
      errorCallback && errorCallback(errorMsg);
    }
  );
}

  // 下载文件
  static httpDownload(deviceId: string, filePath: string, callback?: Function, errorCallback?: Function) {
    const params = new URLSearchParams();
    params.append('deviceId', deviceId);
    params.append('path', filePath);
    
    window.open(`/api/removable/download?${params.toString()}`, '_blank');
    callback && callback();
  }

  // 修复的 preview 静态方法 - 使用 HttpBase 实例
  static httpPreview(deviceId: string, filePath: string, callback?: Function, errorCallback?: Function) {
    const httpBase = new HttpBase();
    const params: any = { 
      deviceId,
      path: filePath
    };
    
    httpBase.httpGet(
      '/api/removable/preview',
      params,
      (response: any) => {
        if (response && response.code === "OK") {
          callback && callback(response.data);
        } else {
          const errorMsg = response && (response.message || response.msg) || '请求失败';
          errorCallback && errorCallback(errorMsg);
        }
      },
      (err: any) => {
        const errorMsg = err && (err.message || err.msg) || '网络错误';
        errorCallback && errorCallback(errorMsg);
      }
    );
  }

  // 修复的 rename 静态方法 - 使用 HttpBase 实例
  static httpRename(deviceId: string, oldPath: string, newName: string, callback?: Function, errorCallback?: Function) {
    const httpBase = new HttpBase();
    const params = {
      deviceId,
      oldPath,
      newName
    };
    
    httpBase.httpPost(
      '/api/removable/rename',
      params,
      (response: any) => {
        if (response && response.code === "OK") {
          callback && callback(response.data);
        } else {
          const errorMsg = response && (response.message || response.msg) || '请求失败';
          errorCallback && errorCallback(errorMsg);
        }
      },
      (err: any) => {
        const errorMsg = err && (err.message || err.msg) || '网络错误';
        errorCallback && errorCallback(errorMsg);
      }
    );
  }
  
  public static formatSizeStatic(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }
  
  // 或者修改原来的私有方法为公有
  public formatSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }
  
  // 修复的 upload 静态方法 - 使用 HttpBase 实例
  static httpUpload(deviceId: string, targetDir: string, file: File, callback?: Function, errorCallback?: Function) {
    const httpBase = new HttpBase();
    const formData = new FormData();
    formData.append('deviceId', deviceId);
    formData.append('targetDir', targetDir);
    formData.append('file', file);
    
    httpBase.httpPost(
      '/api/removable/upload',
      formData,
      (response: any) => {
        if (response && response.code === "OK") {
          callback && callback(response.data);
        } else {
          const errorMsg = response && (response.message || response.msg) || '请求失败';
          errorCallback && errorCallback(errorMsg);
        }
      },
      (err: any) => {
        const errorMsg = err && (err.message || err.msg) || '网络错误';
        errorCallback && errorCallback(errorMsg);
      },
      undefined, // finallyCallback
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
  }

  // 修复的 logs 静态方法 - 使用 HttpBase 实例
  static httpLogs(page: number, pageSize: number, callback?: Function, errorCallback?: Function) {
    const httpBase = new HttpBase();
    const params = {
      page,
      pageSize
    };
    
    httpBase.httpGet(
      '/api/removable/logs',
      params,
      (response: any) => {
        if (response && response.code === "OK") {
          callback && callback(response.data);
        } else {
          const errorMsg = response && (response.message || response.msg) || '请求失败';
          errorCallback && errorCallback(errorMsg);
        }
      },
      (err: any) => {
        const errorMsg = err && (err.message || err.msg) || '网络错误';
        errorCallback && errorCallback(errorMsg);
      }
    );
  }
}